/**
 * MyOutlookConnect — per-rep Microsoft 365 OAuth UI (BW-05.B, Feb 2026).
 *
 * Each rep clicks Connect → popup opens MS consent screen → token persists
 * to users.m365_oauth_tokens → popup postMessages back here → status refreshes.
 *
 * Mirror of MyRingCentralConnect so the UX is identical across the two
 * comms providers. Once a rep is connected, the CRM polls THEIR mailbox
 * (and only theirs) on a background timer — see the M365 poller for the
 * server-side scheduling.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import {
  Mail, CheckCircle2, Loader2, RefreshCw, LogOut, Inbox, Send,
} from "lucide-react";
import BulkEmailPanel from "./BulkEmailPanel";

export default function MyOutlookConnect({ token, role }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [mailboxTest, setMailboxTest] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  // Iter 273 (Jul 2026) — bulk mailer drawer state. Gated by
  // status.connected so the button never appears when the rep
  // hasn't linked their mailbox.
  const [bulkOpen, setBulkOpen] = useState(false);
  const h = { Authorization: `Bearer ${token}` };

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/integrations/m365/oauth/status`, { headers: h });
      setStatus(r.data);
    } catch {
      setStatus({ configured: false, connected: false });
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    const onMsg = (ev) => {
      const d = ev?.data;
      if (!d || typeof d !== "object" || d.type !== "m365-oauth") return;
      if (d.success) {
        toast.success("Outlook connected");
        fetchStatus();
      } else {
        toast.error(d.message || "Outlook connection failed");
      }
      setConnecting(false);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [fetchStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const r = await axios.get(`${API}/integrations/m365/oauth/url`, { headers: h });
      if (!r.data?.ready) {
        toast.error(r.data?.message || "M365 is not configured.");
        setConnecting(false);
        return;
      }
      const w = window.open(r.data.url, "m365-oauth", "width=560,height=720,left=200,top=120");
      if (!w) {
        toast.error("Popup blocked. Allow popups for this site and try again.");
        setConnecting(false);
        return;
      }
      const guard = setInterval(() => {
        if (w.closed) {
          clearInterval(guard);
          setConnecting(false);
          fetchStatus();
        }
      }, 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start Outlook connection.");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/integrations/m365/oauth/disconnect`, {}, { headers: h });
      toast.success("Outlook disconnected");
      setMailboxTest(null);
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disconnect failed");
    }
  };

  const handleTestMailbox = async () => {
    setTesting(true);
    setMailboxTest(null);
    try {
      const r = await axios.post(`${API}/integrations/m365/test-mailbox`, {}, { headers: h });
      setMailboxTest(r.data);
      if (r.data?.ok) toast.success(`Fetched ${r.data.count} recent emails from your inbox`);
    } catch (e) {
      setMailboxTest(e.response?.data || { ok: false, message: e.message });
    }
    setTesting(false);
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await axios.post(`${API}/integrations/m365/sync-now`, {}, { headers: h });
      setSyncResult(r.data?.stats || r.data);
      const m = r.data?.stats?.total_matched ?? 0;
      const f = r.data?.stats?.total_fetched ?? 0;
      toast.success(`Synced ${f} emails · ${m} linked to leads`);
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.message || "Sync failed");
      setSyncResult(e.response?.data || { error: e.message });
    }
    setSyncing(false);
  };

  if (loading) {
    return (
      <div className="card" style={{ padding: 16, marginTop: 14, display: "flex", alignItems: "center", gap: 10 }} data-testid="my-outlook-loading">
        <Loader2 style={{ width: 14, height: 14, color: "#888", animation: "spin 1s linear infinite" }} />
        <span style={{ fontSize: 12, color: "#888" }}>Loading your Outlook status…</span>
      </div>
    );
  }
  if (!status?.configured) {
    // Iter 235 (July 2026) — surface a subtle admin-only hint when
    // this deployment lacks the MS_* env vars. Otherwise silently
    // hide (unchanged behavior for regular reps).
    const isAdmin = ["super_admin", "crm_admin"].includes(role);
    if (!isAdmin) return null;
    return (
      <div
        className="card"
        data-testid="my-outlook-not-configured-admin"
        style={{
          padding: "12px 14px",
          marginTop: 14,
          background: "#FFFBEB",
          border: "0.5px solid #FDE68A",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Mail style={{ width: 14, height: 14, color: "#B45309" }} />
          <div style={{ fontSize: 12, fontWeight: 600, color: "#78350F" }}>
            Microsoft 365 not configured on this deployment
          </div>
        </div>
        <div style={{ fontSize: 11, color: "#78350F", marginTop: 6, lineHeight: 1.5 }}>
          The M365 connect tile is hidden because the backend env vars aren&apos;t set.
          To enable Outlook email sync + calendar invites, set&nbsp;
          <code style={{ background: "#FEF3C7", padding: "1px 4px", borderRadius: 3 }}>MS_TENANT_ID</code>,&nbsp;
          <code style={{ background: "#FEF3C7", padding: "1px 4px", borderRadius: 3 }}>MS_CLIENT_ID</code>,&nbsp;
          <code style={{ background: "#FEF3C7", padding: "1px 4px", borderRadius: 3 }}>MS_CLIENT_SECRET</code>,&nbsp;
          <code style={{ background: "#FEF3C7", padding: "1px 4px", borderRadius: 3 }}>MS_REDIRECT_URI</code>
          &nbsp;in backend/.env and restart the backend.
        </div>
      </div>
    );
  }

  const connected = !!status?.connected;

  return (
    <div className="card" style={{ padding: "18px 22px", marginTop: 14 }} data-testid="my-outlook-panel">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Mail style={{ width: 16, height: 16, color: "#2564eb" }} />
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>My Outlook (Microsoft 365)</h3>
        {connected && (
          <span style={{ padding: "2px 10px", borderRadius: 10, fontSize: 10, fontWeight: 600, background: "#DAFBE8", color: "#1D9E75", marginLeft: 4 }} data-testid="my-outlook-connected-badge">
            Connected
          </span>
        )}
      </div>

      {connected ? (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginBottom: 4 }}>
                The CRM is syncing your Outlook inbox + Sent Items in the background.
                Emails sent to/from leads will appear in their timeline automatically.
              </div>
              <div style={{ fontSize: 11, color: "#475569", marginTop: 6 }}>
                <span style={{ fontWeight: 600 }}>Connected as:</span>{" "}
                <span data-testid="my-outlook-owner-email">{status?.owner_email || status?.owner_name || "—"}</span>
              </div>
              {status?.connected_at && (
                <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 4 }}>
                  Connected {new Date(status.connected_at).toLocaleString()}
                  {status?.last_synced_at && (
                    <> · Last sync <span data-testid="my-outlook-last-sync">{new Date(status.last_synced_at).toLocaleString()}</span></>
                  )}
                  {!status?.last_synced_at && (
                    <span style={{ color: "#94a3b8" }}> · Awaiting first sync</span>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                onClick={handleSyncNow}
                disabled={syncing}
                data-testid="my-outlook-sync-now-btn"
                style={{ background: "#10b981", color: "#fff", border: 0, borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: syncing ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit" }}
              >
                {syncing
                  ? <Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} />
                  : <RefreshCw style={{ width: 12, height: 12 }} />}
                Sync now
              </button>
              <button
                onClick={() => setBulkOpen(true)}
                data-testid="my-outlook-bulk-email-btn"
                title="Send a one-shot broadcast email from your connected Outlook (20 / day)"
                style={{ background: "#2563EB", color: "#fff", border: 0, borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit" }}
              >
                <Send style={{ width: 12, height: 12 }} /> Send bulk email
              </button>
              <button
                onClick={handleTestMailbox}
                disabled={testing}
                data-testid="my-outlook-test-btn"
                style={{ background: "#fff", border: "0.5px solid var(--border)", borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: testing ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 6, color: "#334155", fontFamily: "inherit" }}
              >
                {testing
                  ? <Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} />
                  : <Inbox style={{ width: 12, height: 12 }} />}
                Fetch 5 recent emails
              </button>
              <button
                onClick={handleConnect}
                disabled={connecting}
                data-testid="my-outlook-reconnect-btn"
                style={{ background: "#fff", border: "0.5px solid var(--border)", borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: connecting ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 6, color: "#334155", fontFamily: "inherit" }}
              >
                <RefreshCw style={{ width: 12, height: 12 }} /> Reconnect
              </button>
              <button
                onClick={handleDisconnect}
                data-testid="my-outlook-disconnect-btn"
                style={{ background: "#fff", border: "0.5px solid #ffccc7", borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, color: "#A32D2D", fontFamily: "inherit" }}
              >
                <LogOut style={{ width: 12, height: 12 }} /> Disconnect
              </button>
            </div>
          </div>

          {syncResult && (
            <div style={{ marginTop: 12, background: "#f0f9ff", border: "0.5px solid #7dd3fc", borderRadius: 8, padding: 10, fontSize: 11, color: "#0c4a6e" }} data-testid="my-outlook-sync-result">
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Sync result</div>
              {syncResult.total_fetched != null ? (
                <div>
                  Fetched <strong>{syncResult.total_fetched}</strong> emails ·
                  Linked <strong>{syncResult.total_matched}</strong> to leads ·
                  Inbox {syncResult.inbox?.fetched ?? 0} / {syncResult.inbox?.matched ?? 0} matched ·
                  Sent {syncResult.sent?.fetched ?? 0} / {syncResult.sent?.matched ?? 0} matched
                </div>
              ) : (
                <div style={{ color: "#991b1b" }}>{syncResult.error || syncResult.skipped || "Sync skipped"}</div>
              )}
            </div>
          )}

          {mailboxTest && (
            <div style={{ marginTop: 12 }} data-testid="my-outlook-mailbox-test-result">
              {mailboxTest.ok ? (
                <div style={{ background: "#f0fdf4", border: "0.5px solid #86efac", borderRadius: 8, padding: 10 }}>
                  <div style={{ fontWeight: 600, color: "#15803d", fontSize: 11, marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
                    <CheckCircle2 style={{ width: 12, height: 12 }} /> {mailboxTest.count} recent emails fetched
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: "#475569" }}>
                    {(mailboxTest.messages || []).map((m) => (
                      <li key={m.id} style={{ marginBottom: 4 }}>
                        <strong>{m.subject || "(no subject)"}</strong>
                        <span style={{ color: "#94a3b8" }}> · from {m.from || "—"} · {m.received ? new Date(m.received).toLocaleString() : "—"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div style={{ background: "#fef2f2", border: "0.5px solid #fecaca", borderRadius: 8, padding: 10, fontSize: 11, color: "#991b1b" }}>
                  {mailboxTest.hint || mailboxTest.message || "Failed to fetch mailbox"}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginBottom: 4 }}>
              Connect your Microsoft 365 / Outlook account so the CRM can sync
              emails to/from your leads into their timeline. The CRM only sees
              YOUR mailbox — not anyone else's.
            </div>
            <div style={{ fontSize: 11, color: "#475569", background: "#f8fafc", borderRadius: 6, padding: "6px 8px", marginTop: 6, lineHeight: 1.5 }}>
              Permissions you'll grant: <strong>Read your mail</strong>, <strong>Send mail as you</strong>, <strong>Maintain access while you're using the CRM</strong>.
            </div>
          </div>
          <button
            onClick={handleConnect}
            disabled={connecting}
            data-testid="my-outlook-connect-btn"
            style={{ background: "#2563eb", color: "#fff", border: 0, borderRadius: 8, padding: "8px 16px", fontSize: 12, fontWeight: 600, cursor: connecting ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit", whiteSpace: "nowrap" }}
          >
            {connecting ? (
              <><Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} /> Connecting…</>
            ) : (
              <><Mail style={{ width: 13, height: 13 }} /> Connect my Outlook</>
            )}
          </button>
        </div>
      )}
      {/* Iter 273 (Jul 2026) — bulk email drawer. Rendered outside the
          connected/disconnected branch so open/close animations aren't
          re-mounted, but the trigger button lives inside the connected
          branch only so it's naturally gated. */}
      <BulkEmailPanel
        token={token}
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        senderEmail={status?.owner_email}
      />
    </div>
  );
}
